import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { 
  Search, Eye, Users, DollarSign, Package, ChevronRight, 
  LayoutGrid, List, Mail, Calendar, RefreshCw, UserPlus, 
  Phone, Sparkles, ArrowUpRight, UserCheck
} from 'lucide-react';
import { formatPrice, formatDate } from '../../lib/utils';
import { cn } from '../../lib/utils';

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('recent');
  const [activeTab, setActiveTab] = useState('clients'); // 'clients' or 'leads'
  const [convertingLead, setConvertingLead] = useState(null);

  useEffect(() => {
    loadData();
  }, [searchQuery, filterTag]);

  const loadData = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filterTag) params.tag = filterTag;
      
      const response = await adminAPI.getAllClients(params);
      const allUsers = response.data;
      
      // Separate leads from clients
      setClients(allUsers.filter(u => !u.is_lead));
      setLeads(allUsers.filter(u => u.is_lead));
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const convertLeadToClient = async (leadId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm('Convert this lead to a client? They will receive a welcome email.')) {
      return;
    }
    
    setConvertingLead(leadId);
    try {
      await adminAPI.post(`/admin/users/${leadId}/convert-to-client`);
      toast.success('Lead converted to client successfully!');
      loadData(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    } finally {
      setConvertingLead(null);
    }
  };

  const currentData = activeTab === 'clients' ? clients : leads;

  const sortedData = [...currentData].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.full_name || '').localeCompare(b.full_name || '');
      case 'revenue':
        return (b.total_revenue || 0) - (a.total_revenue || 0);
      case 'recent':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  const totalClients = clients.length;
  const totalLeads = leads.length;
  const activeClients = clients.filter(c => c.active_services_count > 0).length;
  const totalRevenue = clients.reduce((sum, c) => sum + (c.total_revenue || 0), 0);
  const allTags = [...new Set(currentData.flatMap(c => c.tags || []))];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">Clients & Leads</h1>
            <p className="text-sm text-[#525252] mt-1">
              {totalClients} clients, {totalLeads} leads
            </p>
          </div>
          <button 
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#525252] bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F5F5F5] transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <Users className="w-5 h-5 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">{totalClients}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Clients</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-orange-100 to-pink-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-orange-500 tracking-tight">{totalLeads}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Leads</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center">
                <Package className="w-5 h-5 text-[#16A34A]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">{activeClients}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Active</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#F3E8FF] flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#9333EA]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#9333EA] tracking-tight">{formatPrice(totalRevenue)}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Revenue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-[#EBEBEB]">
          <button
            onClick={() => setActiveTab('clients')}
            className={cn(
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all -mb-px",
              activeTab === 'clients'
                ? "border-[#0A0A0A] text-[#0A0A0A]"
                : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
            )}
          >
            <Users className="w-4 h-4" />
            Clients
            <span className={cn(
              "ml-1 px-2 py-0.5 text-xs rounded-full",
              activeTab === 'clients' ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#525252]"
            )}>
              {totalClients}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={cn(
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all -mb-px",
              activeTab === 'leads'
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
            )}
          >
            <Sparkles className="w-4 h-4" />
            Leads
            <span className={cn(
              "ml-1 px-2 py-0.5 text-xs rounded-full",
              activeTab === 'leads' ? "bg-orange-500 text-white" : "bg-[#F5F5F5] text-[#525252]"
            )}>
              {totalLeads}
            </span>
          </button>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A3A3]" />
              <input
                type="text"
                placeholder={activeTab === 'clients' ? "Search clients..." : "Search leads..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 h-10 pl-10 pr-4 text-sm bg-white border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-[#0A0A0A]"
              />
            </div>
            
            {/* Tag Filter */}
            {allTags.length > 0 && (
              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                className="h-10 px-4 text-sm bg-white border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
              >
                <option value="">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-10 px-4 text-sm bg-white border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
            >
              <option value="recent">Most Recent</option>
              <option value="name">Name A-Z</option>
              {activeTab === 'clients' && <option value="revenue">Highest Revenue</option>}
            </select>

            {/* View Mode */}
            <div className="flex items-center bg-[#F5F5F5] rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  "p-2 rounded-md transition-all",
                  viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                )}
              >
                <List className="w-4 h-4 text-[#525252]" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  "p-2 rounded-md transition-all",
                  viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                )}
              >
                <LayoutGrid className="w-4 h-4 text-[#525252]" />
              </button>
            </div>
          </div>
        </div>

        {/* Data List/Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-black rounded-full animate-spin"></div>
          </div>
        ) : sortedData.length === 0 ? (
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-12 text-center">
            <div className="w-14 h-14 rounded-xl bg-[#F5F5F5] flex items-center justify-center mx-auto mb-4">
              {activeTab === 'clients' ? (
                <Users className="w-7 h-7 text-[#A3A3A3]" />
              ) : (
                <Sparkles className="w-7 h-7 text-orange-300" />
              )}
            </div>
            <h3 className="text-base font-semibold text-[#0A0A0A] mb-2">
              {activeTab === 'clients' ? 'No clients found' : 'No leads yet'}
            </h3>
            <p className="text-sm text-[#525252]">
              {activeTab === 'clients' 
                ? (searchQuery ? 'Try adjusting your search' : 'Clients will appear here when they register')
                : 'Leads from AI chat will appear here when visitors sign up'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedData.map((item) => (
              <div 
                key={item.id} 
                className={cn(
                  "bg-white border rounded-xl p-5 hover:shadow-md hover:border-[#D4D4D4] transition-all",
                  item.is_lead ? "border-orange-200" : "border-[#EBEBEB]"
                )}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-11 h-11 rounded-full flex items-center justify-center text-base font-semibold",
                      item.is_lead 
                        ? "bg-gradient-to-r from-orange-100 to-pink-100 text-orange-600"
                        : "bg-[#F5F5F5] text-[#525252]"
                    )}>
                      {item.full_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-[#0A0A0A]">{item.full_name || 'Unknown'}</p>
                        {item.is_lead && (
                          <span className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full font-medium">
                            LEAD
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#A3A3A3]">{item.email}</p>
                    </div>
                  </div>
                  <Link
                    to={`/admin/clients/${item.id}`}
                    className="p-2 rounded-lg hover:bg-[#F5F5F5] transition-all"
                  >
                    <Eye className="w-4 h-4 text-[#525252]" />
                  </Link>
                </div>
                
                {item.phone && (
                  <div className="flex items-center gap-2 mb-3 text-xs text-[#525252]">
                    <Phone className="w-3 h-3" />
                    {item.phone}
                  </div>
                )}
                
                {item.lead_source && (
                  <div className="flex items-center gap-2 mb-3 text-xs text-orange-500">
                    <Sparkles className="w-3 h-3" />
                    Source: {item.lead_source}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#EBEBEB]">
                  {item.is_lead ? (
                    <>
                      <div>
                        <p className="text-xs text-[#A3A3A3] mb-1">Created</p>
                        <p className="text-sm font-medium text-[#0A0A0A]">{formatDate(item.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#A3A3A3] mb-1">Status</p>
                        <p className="text-sm font-medium text-orange-500">New Lead</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-xs text-[#A3A3A3] mb-1">Revenue</p>
                        <p className="text-sm font-semibold text-[#0A0A0A]">{formatPrice(item.total_revenue || 0)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#A3A3A3] mb-1">Services</p>
                        <p className="text-sm font-semibold text-[#0A0A0A]">{item.active_services_count || 0}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#EBEBEB]">
                  <th className="text-left px-5 py-4 text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">
                    {activeTab === 'clients' ? 'Client' : 'Lead'}
                  </th>
                  <th className="text-left px-5 py-4 text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Contact</th>
                  {activeTab === 'clients' ? (
                    <>
                      <th className="text-left px-5 py-4 text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Revenue</th>
                      <th className="text-left px-5 py-4 text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Services</th>
                    </>
                  ) : (
                    <>
                      <th className="text-left px-5 py-4 text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Source</th>
                      <th className="text-left px-5 py-4 text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Status</th>
                    </>
                  )}
                  <th className="text-left px-5 py-4 text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Joined</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EBEBEB]">
                {sortedData.map((item) => (
                  <tr key={item.id} className="hover:bg-[#FAFAFA] transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold",
                          item.is_lead 
                            ? "bg-gradient-to-r from-orange-100 to-pink-100 text-orange-600"
                            : "bg-[#F5F5F5] text-[#525252]"
                        )}>
                          {item.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#0A0A0A]">{item.full_name || 'Unknown'}</span>
                            {item.is_lead && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded font-medium">
                                LEAD
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="space-y-1">
                        <p className="text-sm text-[#525252]">{item.email}</p>
                        {item.phone && (
                          <p className="text-xs text-[#A3A3A3]">{item.phone}</p>
                        )}
                      </div>
                    </td>
                    {activeTab === 'clients' ? (
                      <>
                        <td className="px-5 py-4">
                          <span className="text-sm font-semibold text-[#0A0A0A]">{formatPrice(item.total_revenue || 0)}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={cn(
                            "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium",
                            item.active_services_count > 0 
                              ? "bg-[#DCFCE7] text-[#16A34A]" 
                              : "bg-[#F5F5F5] text-[#525252]"
                          )}>
                            {item.active_services_count || 0} active
                          </span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 text-sm text-orange-500">
                            <Sparkles className="w-3.5 h-3.5" />
                            {item.lead_source || 'AI Chat'}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <button
                            onClick={(e) => convertLeadToClient(item.id, e)}
                            disabled={convertingLead === item.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50"
                          >
                            {convertingLead === item.id ? (
                              <>
                                <div className="w-3 h-3 border-2 border-green-400 border-t-green-700 rounded-full animate-spin" />
                                Converting...
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-3.5 h-3.5" />
                                Convert to Client
                              </>
                            )}
                          </button>
                        </td>
                      </>
                    )}
                    <td className="px-5 py-4 text-sm text-[#A3A3A3]">{formatDate(item.created_at)}</td>
                    <td className="px-5 py-4">
                      <Link
                        to={`/admin/clients/${item.id}`}
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[#F5F5F5] transition-all"
                      >
                        <ChevronRight className="w-4 h-4 text-[#525252]" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
